import { useCallback } from 'react';

import { UIDAPresentationTypeEnum } from '@kaltura-ott/tvpil';
import { UserActionType } from '@kaltura-ott/tvpil-analytics';
import { RecordingAsset } from '@kaltura-ott/tvpil-shared';

import { DescriptionWithAge } from 'components/widgets/Modal/PinCodeModal/DescriptionWithAge/DescriptionWithAge';
import PinCodeModal from 'components/widgets/Modal/PinCodeModal/PinCodeModal';
import { PinCodeModalTypes } from 'components/widgets/Modal/PinCodeModal/PinCodeModalTypesEnum';
import { useRailContext } from 'components/widgets/Rail/components/RailProvider';
import { useModalContext } from 'context/modal/modal';
import { useStableReactRouter } from 'context/stableReactRouterContext/stableReactRouterContext';
import { useIsUrlToFullPlayer, useParentalPinStore } from 'hooks';
import { userActionEvents } from 'utils';

interface Props {
  recording: RecordingAsset;
  link: string;
}

export const useClickHandler = ({ recording, link }: Props) => {
  const { onCardClick } = useRailContext();

  const { navigate } = useStableReactRouter();
  const parentalPinStore = useParentalPinStore();
  const isUrlToFullPlayer = useIsUrlToFullPlayer(link);
  const modalContext = useModalContext();
  const parentalRating = Number(recording?.parentalRating);

  return useCallback(
    (e: React.MouseEvent<HTMLAnchorElement>) => {
      if (onCardClick) {
        onCardClick(recording.id);
      }

      if (
        isUrlToFullPlayer &&
        parentalRating &&
        parentalPinStore.isBlockedByParentalRating(parentalRating)
      ) {
        e.preventDefault();
        const validateParental = (pin: string) => parentalPinStore.validateParentalRating({ pin, parentalRating });

        modalContext.openModal(
          <PinCodeModal
            callback={(success: boolean) => {
              if (success) {
                navigate(link);
              }
            }}
            context={UIDAPresentationTypeEnum.applications}
            description={parentalRating && <DescriptionWithAge age={parentalRating} />}
            type={PinCodeModalTypes.RestrictedContentParental}
            validatePin={validateParental}
          />,
        );
        return;
      }

      if (link && link.includes('details')) {
        // to send the user event after clicking on an AssetCardLink which should open the details page
        userActionEvents(UserActionType.details, recording.id, recording.type);
      }
    },
    [
      onCardClick,
      isUrlToFullPlayer,
      parentalRating,
      recording.id,
      recording.type,
      parentalPinStore,
      link,
      modalContext,
      navigate,
    ],
  );
};
